import React, { useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    Badge,
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    FormControlLabel,
    IconButton,
    Link,
    MenuItem,
    Popover,
    Select,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import TuneIcon from '@mui/icons-material/Tune';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import {
    getOffsiteCampaignDetails,
    approveCampaignJoinRequest,
    bulkApproveCampaignJoinRequests,
    cancelCampaignInvite,
    getCampaignPendingInvites,
    bulkRejectCampaignJoinRequests,
} from '../../redux/buzzbridge/actions';
import {
    currentCampaignSelector,
    gettingCampaignDetailsSelector,
    approvingRequestSelector,
    bulkApprovingRequestsSelector,
    cancellingInviteSelector,
    gettingPendingInvitesSelector,
    pendingInvitesListSelector,
    pendingInvitesPaginationSelector,
    bulkRejectingRequestsSelector,
} from '../../redux/buzzbridge/selectors';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_OUTLINE_VARIANT,
} from './brandDashboardTokens';
import { StatusChip } from './BrandCampaignDetailPage';
import { ApplicationCard } from './campaignDetail/CampaignParticipants';
import { ApplicationReviewDrawer } from './campaignDetail/ApplicationReviewDrawer';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import type { ICampaignParticipantDetail } from '../../redux/buzzbridge/types';
import { influencerProfileAvatarSx } from '../../components/BuzzBridge/utils/creatorProfileHelpers';
import { useApplicantFavorite } from './campaignDetail/useApplicantFavorite';

export default function BrandCampaignApplicationsPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { campaignId } = useParams<{ campaignId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];

    const campaign = useSelector(currentCampaignSelector);
    const loading = useSelector(gettingCampaignDetailsSelector);
    const approvingRequest = useSelector(approvingRequestSelector);
    const bulkApprovingRequests = useSelector(bulkApprovingRequestsSelector);
    const cancellingInvite = useSelector(cancellingInviteSelector);
    const gettingPendingInvites = useSelector(gettingPendingInvitesSelector);
    const pendingInvitesList = useSelector(pendingInvitesListSelector);
    const pendingInvitesPagination = useSelector(pendingInvitesPaginationSelector);

    const tabParam = searchParams.get('tab');
    const activeTab: 'applications' | 'invites' = tabParam === 'invites' ? 'invites' : 'applications';
    const bulkRejectingRequests = useSelector(bulkRejectingRequestsSelector);

    const [drawerApplicant, setDrawerApplicant] = useState<ICampaignParticipantDetail | null>(null);
    const [drawerInviteMode, setDrawerInviteMode] = useState(false);
    const [confirmReject, setConfirmReject] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [acceptingId, setAcceptingId] = useState<string | null>(null);
    const [cancellingId, setCancellingId] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [inviteSearchInput, setInviteSearchInput] = useState('');
    const [inviteSearch, setInviteSearch] = useState('');
    const [invitePage, setInvitePage] = useState(1);

    // Filter state
    const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
    const [draftEngMin, setDraftEngMin] = useState('');
    const [draftFollowersMin, setDraftFollowersMin] = useState('');
    const [draftFavoritesOnly, setDraftFavoritesOnly] = useState(false);
    const [applied, setApplied] = useState({ engMin: '', followersMin: '', favoritesOnly: false });
    const [favoriteMap, setFavoriteMap] = useState<Record<string, boolean>>({});
    const activeFilterCount = Object.values(applied).filter(v => (typeof v === 'boolean' ? v : Boolean(v))).length;

    const handleFavoriteChange = (influencerId: string, favorited: boolean) => {
        setFavoriteMap(prev => ({ ...prev, [influencerId]: favorited }));
    };

    const isApplicantFavorited = (a: ICampaignParticipantDetail) => {
        if (!a.influencer_id) return false;
        if (a.influencer_id in favoriteMap) return favoriteMap[a.influencer_id];
        return Boolean(a.is_favorited);
    };

    const setActiveTab = (tab: 'applications' | 'invites') => {
        setSearchParams(tab === 'invites' ? { tab: 'invites' } : {}, { replace: true });
        setSelectedIds(new Set());
    };

    useEffect(() => {
        if (campaignId) dispatch(getOffsiteCampaignDetails({ campaignId, asBrandHost: true }));
    }, [campaignId, dispatch]);

    useEffect(() => {
        const t = setTimeout(() => {
            setInviteSearch(inviteSearchInput.trim());
            setInvitePage(1);
        }, 350);
        return () => clearTimeout(t);
    }, [inviteSearchInput]);

    useEffect(() => {
        if (!campaignId || activeTab !== 'invites') return;
        dispatch(
            getCampaignPendingInvites({
                campaignId,
                page: invitePage,
                limit: 20,
                search: inviteSearch || undefined,
            }),
        );
    }, [campaignId, activeTab, invitePage, inviteSearch, dispatch]);

    useEffect(() => {
        if (!approvingRequest && acceptingId) setAcceptingId(null);
    }, [approvingRequest, acceptingId]);

    useEffect(() => {
        if (!cancellingInvite && cancellingId) setCancellingId(null);
    }, [cancellingInvite, cancellingId]);

    const handleAccept = (applicant: ICampaignParticipantDetail) => {
        if (!campaignId) return;
        setAcceptingId(applicant._id);
        dispatch(approveCampaignJoinRequest({ requestId: applicant._id, campaignId }));
    };

    const handleCancelInvite = (invite: ICampaignParticipantDetail) => {
        if (!campaignId) return;
        setCancellingId(invite._id);
        dispatch(cancelCampaignInvite({ participantId: invite._id, campaignId }));
    };

    const openDrawer = (row: ICampaignParticipantDetail, asInvite: boolean) => {
        setDrawerInviteMode(asInvite);
        setDrawerApplicant(row);
    };

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const selectAll = (requests: ICampaignParticipantDetail[]) => {
        setSelectedIds(new Set(requests.map(r => r._id)));
    };

    const clearSelection = () => setSelectedIds(new Set());

    const handleBulkAccept = () => {
        if (!campaignId || bulkApprovingRequests) return;
        dispatch(bulkApproveCampaignJoinRequests({ campaignId, participantIds: [...selectedIds] }));
        clearSelection();
    };

    const handleBulkReject = () => {
        if (!campaignId || bulkRejectingRequests) return;
        setRejectReason('');
        setConfirmReject(true);
    };

    const confirmBulkReject = () => {
        if (!campaignId) return;
        dispatch(
            bulkRejectCampaignJoinRequests({
                campaignId,
                participantIds: [...selectedIds],
                ...(rejectReason.trim() ? { reason: rejectReason.trim() } : {}),
            }),
        );
        setConfirmReject(false);
        setRejectReason('');
        clearSelection();
    };

    const pendingRequests = useMemo<ICampaignParticipantDetail[]>(() => campaign?.pending_requests ?? [], [campaign]);
    const embeddedPendingInvites = useMemo<ICampaignParticipantDetail[]>(
        () => campaign?.pending_invites ?? [],
        [campaign],
    );
    const inviteRows = activeTab === 'invites' ? pendingInvitesList : embeddedPendingInvites;

    const filteredRequests = useMemo(() => {
        return pendingRequests.filter(a => {
            if (applied.engMin && (a.engagementRate ?? 0) < parseFloat(applied.engMin)) return false;
            if (applied.followersMin && (a.followers ?? 0) < parseInt(applied.followersMin)) return false;
            if (applied.favoritesOnly && !isApplicantFavorited(a)) return false;
            return true;
        });
        // favoriteMap intentionally included so favourites-only updates live
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingRequests, applied, favoriteMap]);

    const filteredInvites = useMemo(() => {
        return inviteRows.filter(a => {
            if (applied.engMin && (a.engagementRate ?? 0) < parseFloat(applied.engMin)) return false;
            if (applied.followersMin && (a.followers ?? 0) < parseInt(applied.followersMin)) return false;
            if (applied.favoritesOnly && !isApplicantFavorited(a)) return false;
            return true;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteRows, applied, favoriteMap]);

    const pendingCount = activeTab === 'invites' ? inviteRows.length : pendingRequests.length;
    const filteredRows = activeTab === 'invites' ? filteredInvites : filteredRequests;

    const handleFilterApply = () => {
        setApplied({
            engMin: draftEngMin,
            followersMin: draftFollowersMin,
            favoritesOnly: draftFavoritesOnly,
        });
        setFilterAnchor(null);
    };

    const handleFilterClear = () => {
        setDraftEngMin('');
        setDraftFollowersMin('');
        setDraftFavoritesOnly(false);
        setApplied({ engMin: '', followersMin: '', favoritesOnly: false });
        setFilterAnchor(null);
    };

    if (loading || !campaign) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
            </Box>
        );
    }

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
            {/* Bulk action overlay */}
            {(bulkApprovingRequests || bulkRejectingRequests) && (
                <Box
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1400,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        backdropFilter: 'blur(4px)',
                    }}>
                    <CircularProgress size={52} thickness={3} sx={{ color: BRAND_LUXE_PRIMARY }} />
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                            color: 'rgba(255,255,255,0.7)',
                            letterSpacing: '0.04em',
                        }}>
                        {bulkRejectingRequests ? 'Rejecting…' : 'Approving…'}
                    </Typography>
                </Box>
            )}

            {/* Breadcrumbs */}
            <Breadcrumbs
                aria-label="breadcrumb"
                sx={{ mb: 3, alignSelf: 'flex-start', '& .MuiBreadcrumbs-separator': { color: t.form_label } }}>
                <Link
                    component={RouterLink}
                    to={ROUTES.BRAND_CAMPAIGNS}
                    underline="hover"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: t.primary_white,
                        '&:hover': { color: BRAND_LUXE_PRIMARY },
                    }}>
                    Campaigns
                </Link>
                <Link
                    component={RouterLink}
                    to={ROUTES.BRAND_CAMPAIGN_DETAIL.replace(':campaignId', campaignId!)}
                    underline="hover"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: t.primary_white,
                        '&:hover': { color: BRAND_LUXE_PRIMARY },
                    }}>
                    {campaign.title}
                </Link>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: BRAND_LUXE_PRIMARY }}>
                    {activeTab === 'invites' ? 'Invites' : 'Applications'}
                </Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 5 }}>
                <IconButton
                    onClick={() => navigate(ROUTES.BRAND_CAMPAIGN_DETAIL.replace(':campaignId', campaignId!))}
                    sx={{
                        color: BRAND_LUXE_PRIMARY,
                        bgcolor: 'rgba(255,201,101,0.08)',
                        '&:hover': { bgcolor: 'rgba(255,201,101,0.15)' },
                    }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: 22, md: 28 },
                                color: '#fff',
                                letterSpacing: '-0.02em',
                            }}>
                            {campaign.title}
                        </Typography>
                        <StatusChip status={campaign.status} />
                        {campaign.funded && (
                            <Chip
                                label="Funded"
                                size="small"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 11,
                                    height: 24,
                                    bgcolor: `${BRAND_LUXE_SECONDARY}18`,
                                    color: BRAND_LUXE_SECONDARY,
                                }}
                            />
                        )}
                    </Box>
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>
                        Created{' '}
                        {new Date(campaign.createdAt!).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                        })}
                    </Typography>
                </Box>
            </Box>

            {/* Feature image banner */}
            {campaign.feature_image && (
                <Box
                    sx={{
                        height: 200,
                        borderRadius: 3,
                        overflow: 'hidden',
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        mb: 4,
                    }}>
                    <Box
                        component="img"
                        src={campaign.feature_image}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </Box>
            )}

            {/* Section heading */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        onClick={() => setActiveTab('applications')}
                        sx={{
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 18,
                            color: activeTab === 'applications' ? '#fff' : 'rgba(255,255,255,0.4)',
                            px: 0,
                            minWidth: 0,
                            '&:hover': { bgcolor: 'transparent', color: '#fff' },
                        }}>
                        Applications
                    </Button>
                    <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: 18 }}>·</Typography>
                    <Button
                        onClick={() => setActiveTab('invites')}
                        sx={{
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 18,
                            color: activeTab === 'invites' ? '#fff' : 'rgba(255,255,255,0.4)',
                            px: 0,
                            minWidth: 0,
                            '&:hover': { bgcolor: 'transparent', color: '#fff' },
                        }}>
                        Invites
                    </Button>
                </Box>
                {pendingCount > 0 && (
                    <Chip
                        label={`${pendingCount} PENDING`}
                        size="small"
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 10,
                            height: 24,
                            letterSpacing: '0.08em',
                            bgcolor: `${BRAND_LUXE_SECONDARY}18`,
                            color: BRAND_LUXE_SECONDARY,
                        }}
                    />
                )}
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {activeTab === 'invites' && (
                        <Box
                            component="input"
                            value={inviteSearchInput}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInviteSearchInput(e.target.value)}
                            placeholder="Search invites…"
                            sx={{
                                mr: 1,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 999,
                                border: '1px solid rgba(255,255,255,0.12)',
                                bgcolor: 'rgba(255,255,255,0.04)',
                                color: '#fff',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 13,
                                outline: 'none',
                                width: { xs: 140, sm: 200 },
                                '&::placeholder': { color: 'rgba(255,255,255,0.35)' },
                            }}
                        />
                    )}
                    <Badge
                        badgeContent={activeFilterCount}
                        color="warning"
                        sx={{
                            '& .MuiBadge-badge': {
                                bgcolor: BRAND_LUXE_PRIMARY,
                                color: '#392700',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 9,
                                minWidth: 16,
                                height: 16,
                                padding: '0 4px',
                            },
                        }}>
                        <Tooltip title="Filter">
                            <IconButton
                                onClick={e => {
                                    setDraftEngMin(applied.engMin);
                                    setDraftFollowersMin(applied.followersMin);
                                    setDraftFavoritesOnly(applied.favoritesOnly);
                                    setFilterAnchor(e.currentTarget);
                                }}
                                size="small"
                                sx={{
                                    color: activeFilterCount > 0 ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.3)',
                                    bgcolor: activeFilterCount > 0 ? `${BRAND_LUXE_PRIMARY}12` : 'transparent',
                                    border:
                                        activeFilterCount > 0
                                            ? `1px solid ${BRAND_LUXE_PRIMARY}30`
                                            : '1px solid transparent',
                                    '&:hover': { bgcolor: `${BRAND_LUXE_PRIMARY}18`, color: BRAND_LUXE_PRIMARY },
                                }}>
                                <TuneIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Badge>
                    <Box sx={{ width: 1, bgcolor: 'rgba(255,255,255,0.08)', mx: 0.5, alignSelf: 'stretch' }} />
                    <Tooltip title="Grid view">
                        <IconButton
                            onClick={() => setViewMode('grid')}
                            size="small"
                            sx={{
                                color: viewMode === 'grid' ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.3)',
                                bgcolor: viewMode === 'grid' ? `${BRAND_LUXE_PRIMARY}12` : 'transparent',
                                '&:hover': { bgcolor: `${BRAND_LUXE_PRIMARY}18` },
                            }}>
                            <GridViewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="List view">
                        <IconButton
                            onClick={() => setViewMode('list')}
                            size="small"
                            sx={{
                                color: viewMode === 'list' ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.3)',
                                bgcolor: viewMode === 'list' ? `${BRAND_LUXE_PRIMARY}12` : 'transparent',
                                '&:hover': { bgcolor: `${BRAND_LUXE_PRIMARY}18` },
                            }}>
                            <ViewListIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Filter popover */}
            <Popover
                open={Boolean(filterAnchor)}
                anchorEl={filterAnchor}
                onClose={handleFilterApply}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        bgcolor: '#1a1919',
                        border: '1px solid rgba(255,255,255,0.07)',
                        borderRadius: 3,
                        p: 2.5,
                        width: 260,
                    },
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: '#fff',
                        mb: 2,
                        letterSpacing: '-0.01em',
                    }}>
                    Filter applicants
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 10,
                                color: 'rgba(255,255,255,0.35)',
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                mb: 0.75,
                            }}>
                            Engagement rate
                        </Typography>
                        <Select
                            size="small"
                            displayEmpty
                            value={draftEngMin}
                            onChange={e => setDraftEngMin(e.target.value)}
                            sx={{
                                width: '100%',
                                bgcolor: 'rgba(255,255,255,0.05)',
                                borderRadius: 2,
                                fontSize: 13,
                                color: '#fff',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: `${BRAND_LUXE_PRIMARY}60`,
                                },
                                '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.35)' },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        bgcolor: '#1a1919',
                                        border: '1px solid rgba(255,255,255,0.07)',
                                        borderRadius: 2,
                                        '& .MuiMenuItem-root': {
                                            fontSize: 13,
                                            color: '#fff',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                                            '&.Mui-selected': { bgcolor: `${BRAND_LUXE_PRIMARY}15` },
                                        },
                                    },
                                },
                            }}>
                            <MenuItem value="">Any</MenuItem>
                            <MenuItem value="1">1%+</MenuItem>
                            <MenuItem value="2">2%+</MenuItem>
                            <MenuItem value="3">3%+</MenuItem>
                            <MenuItem value="5">5%+</MenuItem>
                            <MenuItem value="8">8%+</MenuItem>
                            <MenuItem value="10">10%+</MenuItem>
                        </Select>
                    </Box>

                    <Box>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 10,
                                color: 'rgba(255,255,255,0.35)',
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                mb: 0.75,
                            }}>
                            Followers
                        </Typography>
                        <Select
                            size="small"
                            displayEmpty
                            value={draftFollowersMin}
                            onChange={e => setDraftFollowersMin(e.target.value)}
                            sx={{
                                width: '100%',
                                bgcolor: 'rgba(255,255,255,0.05)',
                                borderRadius: 2,
                                fontSize: 13,
                                color: '#fff',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: `${BRAND_LUXE_PRIMARY}60`,
                                },
                                '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.35)' },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        bgcolor: '#1a1919',
                                        border: '1px solid rgba(255,255,255,0.07)',
                                        borderRadius: 2,
                                        '& .MuiMenuItem-root': {
                                            fontSize: 13,
                                            color: '#fff',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                                            '&.Mui-selected': { bgcolor: `${BRAND_LUXE_PRIMARY}15` },
                                        },
                                    },
                                },
                            }}>
                            <MenuItem value="">Any</MenuItem>
                            <MenuItem value="1000">1K+</MenuItem>
                            <MenuItem value="10000">10K+</MenuItem>
                            <MenuItem value="50000">50K+</MenuItem>
                            <MenuItem value="100000">100K+</MenuItem>
                            <MenuItem value="500000">500K+</MenuItem>
                            <MenuItem value="1000000">1M+</MenuItem>
                        </Select>
                    </Box>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={draftFavoritesOnly}
                                onChange={(_, v) => setDraftFavoritesOnly(v)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#ff69b4' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#ff69b4',
                                    },
                                }}
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                {draftFavoritesOnly ? (
                                    <FavoriteIcon sx={{ fontSize: 14, color: '#ff69b4' }} />
                                ) : (
                                    <FavoriteBorderIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.45)' }} />
                                )}
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 13,
                                        color: '#fff',
                                    }}>
                                    Favorites only
                                </Typography>
                            </Box>
                        }
                        sx={{ m: 0, mt: 0.5 }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                        fullWidth
                        size="small"
                        onClick={handleFilterClear}
                        sx={{
                            borderRadius: 999,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 12,
                            color: 'rgba(255,255,255,0.4)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', color: '#fff' },
                        }}>
                        Clear
                    </Button>
                    <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        onClick={handleFilterApply}
                        sx={{
                            borderRadius: 999,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 12,
                            bgcolor: BRAND_LUXE_PRIMARY,
                            color: '#392700',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#feb700', boxShadow: 'none' },
                        }}>
                        Apply
                    </Button>
                </Box>
            </Popover>

            {/* Bulk action bar */}
            <Collapse in={activeTab === 'applications' && selectedIds.size > 0} unmountOnExit>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 2,
                        px: { xs: 1.5, md: 2.5 },
                        py: { xs: 1, md: 1.5 },
                        borderRadius: 2,
                        bgcolor: `${BRAND_LUXE_PRIMARY}10`,
                        border: `1px solid ${BRAND_LUXE_PRIMARY}30`,
                    }}>
                    {/* Icon = select all / deselect all toggle */}
                    <Tooltip title={selectedIds.size === filteredRows.length ? 'Deselect all' : 'Select all'}>
                        <IconButton
                            onClick={() =>
                                selectedIds.size === filteredRows.length ? clearSelection() : selectAll(filteredRows)
                            }
                            size="small"
                            sx={{
                                p: 0.5,
                                color: BRAND_LUXE_PRIMARY,
                                '&:hover': { bgcolor: `${BRAND_LUXE_PRIMARY}18` },
                            }}>
                            <DoneAllIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>

                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 13, md: 14 },
                            color: '#fff',
                            whiteSpace: 'nowrap',
                        }}>
                        {selectedIds.size} selected
                    </Typography>

                    {/* Spacer */}
                    <Box sx={{ flex: 1 }} />

                    {/* Reject button */}
                    <Button
                        variant="outlined"
                        disabled={bulkRejectingRequests || bulkApprovingRequests}
                        onClick={handleBulkReject}
                        sx={{
                            borderRadius: 999,
                            textTransform: 'uppercase',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 10, md: 11 },
                            letterSpacing: '0.08em',
                            px: { xs: 2, md: 2.5 },
                            py: { xs: 0.6, md: 0.75 },
                            borderColor: 'rgba(255,80,80,0.4)',
                            color: '#ff5050',
                            whiteSpace: 'nowrap',
                            '&:hover': { borderColor: '#ff5050', bgcolor: 'rgba(255,80,80,0.08)' },
                            '&.Mui-disabled': { borderColor: 'rgba(255,80,80,0.15)', color: 'rgba(255,80,80,0.3)' },
                        }}>
                        {bulkRejectingRequests ? (
                            <CircularProgress size={13} thickness={5} sx={{ color: '#ff5050' }} />
                        ) : (
                            `Reject ${selectedIds.size}`
                        )}
                    </Button>

                    {/* Accept button */}
                    <Button
                        variant="contained"
                        disabled={bulkApprovingRequests || bulkRejectingRequests}
                        onClick={handleBulkAccept}
                        sx={{
                            borderRadius: 999,
                            textTransform: 'uppercase',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 10, md: 11 },
                            letterSpacing: '0.08em',
                            px: { xs: 2, md: 2.5 },
                            py: { xs: 0.6, md: 0.75 },
                            bgcolor: BRAND_LUXE_PRIMARY,
                            color: '#392700',
                            boxShadow: 'none',
                            whiteSpace: 'nowrap',
                            '&:hover': { bgcolor: '#feb700', boxShadow: '0 4px 12px rgba(255,193,7,0.2)' },
                            '&.Mui-disabled': { bgcolor: `${BRAND_LUXE_PRIMARY}50`, color: '#39270060' },
                        }}>
                        {bulkApprovingRequests ? (
                            <CircularProgress size={13} thickness={5} sx={{ color: '#392700' }} />
                        ) : (
                            `Accept ${selectedIds.size}`
                        )}
                    </Button>

                    {/* Clear */}
                    <IconButton
                        onClick={clearSelection}
                        size="small"
                        sx={{ color: 'rgba(255,255,255,0.4)', p: 0.5, '&:hover': { color: '#fff' } }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </Collapse>

            {/* Applications / Invites list/grid */}
            {gettingPendingInvites && activeTab === 'invites' ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
                </Box>
            ) : filteredRows.length === 0 ? (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, borderRadius: 2, p: 6, textAlign: 'center' }}>
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>
                        {pendingCount === 0
                            ? activeTab === 'invites'
                                ? 'No pending invites'
                                : 'No pending applications'
                            : 'No applicants match the current filters'}
                    </Typography>
                    {pendingCount > 0 && activeFilterCount > 0 && (
                        <Button
                            size="small"
                            onClick={handleFilterClear}
                            sx={{
                                mt: 1.5,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                color: BRAND_LUXE_PRIMARY,
                            }}>
                            Clear filters
                        </Button>
                    )}
                </Box>
            ) : viewMode === 'grid' ? (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                        gap: 2,
                    }}>
                    {filteredRows.map(a => (
                        <ApplicationCard
                            key={a._id}
                            applicant={a}
                            campaignId={campaignId!}
                            onReview={() => openDrawer(a, activeTab === 'invites')}
                            onAccept={() => handleAccept(a)}
                            onCancelInvite={activeTab === 'invites' ? () => handleCancelInvite(a) : undefined}
                            isAccepting={
                                activeTab === 'invites'
                                    ? cancellingId === a._id && cancellingInvite
                                    : acceptingId === a._id && approvingRequest
                            }
                            selected={activeTab === 'applications' ? selectedIds.has(a._id) : undefined}
                            onToggleSelect={activeTab === 'applications' ? () => toggleSelect(a._id) : undefined}
                            onFavoriteChange={handleFavoriteChange}
                        />
                    ))}
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {filteredRows.map(a => (
                        <ApplicationListRow
                            key={a._id}
                            applicant={a}
                            campaignId={campaignId!}
                            onReview={() => openDrawer(a, activeTab === 'invites')}
                            onAccept={() => (activeTab === 'invites' ? handleCancelInvite(a) : handleAccept(a))}
                            acceptLabel={activeTab === 'invites' ? 'Cancel' : 'Accept'}
                            isAccepting={
                                activeTab === 'invites'
                                    ? cancellingId === a._id && cancellingInvite
                                    : acceptingId === a._id && approvingRequest
                            }
                            selected={activeTab === 'applications' ? selectedIds.has(a._id) : undefined}
                            onToggleSelect={activeTab === 'applications' ? () => toggleSelect(a._id) : undefined}
                            onFavoriteChange={handleFavoriteChange}
                        />
                    ))}
                </Box>
            )}

            {activeTab === 'invites' && pendingInvitesPagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
                    <Button
                        disabled={invitePage <= 1}
                        onClick={() => setInvitePage(p => Math.max(1, p - 1))}
                        sx={{ color: BRAND_LUXE_PRIMARY, textTransform: 'none' }}>
                        Previous
                    </Button>
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', alignSelf: 'center', fontSize: 13 }}>
                        Page {pendingInvitesPagination.page} of {pendingInvitesPagination.totalPages}
                    </Typography>
                    <Button
                        disabled={!pendingInvitesPagination.hasMore}
                        onClick={() => setInvitePage(p => p + 1)}
                        sx={{ color: BRAND_LUXE_PRIMARY, textTransform: 'none' }}>
                        Next
                    </Button>
                </Box>
            )}

            <ConfirmDialog
                open={confirmReject}
                title={`Reject ${selectedIds.size} applicant${selectedIds.size !== 1 ? 's' : ''}?`}
                description="These applicants will be notified that their application was not successful. This cannot be undone."
                confirmLabel="Reject"
                destructive
                loading={bulkRejectingRequests}
                onConfirm={confirmBulkReject}
                onCancel={() => {
                    setConfirmReject(false);
                    setRejectReason('');
                }}>
                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', mb: 0.75 }}>
                        Reason for rejection{' '}
                        <Box component="span" sx={{ color: 'rgba(255,255,255,0.25)' }}>
                            (optional)
                        </Box>
                    </Typography>
                    <Box
                        component="textarea"
                        value={rejectReason}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRejectReason(e.target.value)}
                        placeholder="e.g. Your profile does not match the requirements for this campaign."
                        rows={3}
                        sx={{
                            width: '100%',
                            resize: 'none',
                            bgcolor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 1.5,
                            color: '#fff',
                            fontSize: 13,
                            fontFamily: 'inherit',
                            p: 1.5,
                            outline: 'none',
                            boxSizing: 'border-box',
                            '&::placeholder': { color: 'rgba(255,255,255,0.25)' },
                            '&:focus': { borderColor: 'rgba(255,255,255,0.25)' },
                        }}
                    />
                </Box>
            </ConfirmDialog>

            <ApplicationReviewDrawer
                open={Boolean(drawerApplicant)}
                onClose={() => setDrawerApplicant(null)}
                applicant={drawerApplicant}
                campaignId={campaignId!}
                inviteMode={drawerInviteMode}
            />
        </Box>
    );
}

function tagLabel(tag: string | { main: string; sub?: string }): string {
    if (typeof tag === 'string') return tag;
    return tag.sub ? `${tag.main} · ${tag.sub}` : tag.main;
}

function ApplicationListRow({
    applicant,
    campaignId,
    onReview,
    onAccept,
    acceptLabel = 'Accept',
    isAccepting,
    selected,
    onToggleSelect,
    onFavoriteChange,
}: {
    applicant: ICampaignParticipantDetail;
    campaignId: string;
    onReview: () => void;
    onAccept: () => void;
    acceptLabel?: string;
    isAccepting?: boolean;
    selected?: boolean;
    onToggleSelect?: () => void;
    onFavoriteChange?: (influencerId: string, favorited: boolean) => void;
}) {
    const isCancel = acceptLabel.toLowerCase() === 'cancel';
    const safeTags = applicant.tags || [];
    const initials =
        (applicant.name || '')
            .split(' ')
            .map(w => w[0])
            .join('')
            .slice(0, 2) || 'NA';
    const { favorited, toggleFavorite } = useApplicantFavorite(
        campaignId,
        applicant.influencer_id,
        applicant.is_favorited,
    );

    const handleFavorite = async (e: React.MouseEvent) => {
        const next = !favorited;
        if (applicant.influencer_id) onFavoriteChange?.(applicant.influencer_id, next);
        const ok = await toggleFavorite(e);
        if (!ok && applicant.influencer_id) onFavoriteChange?.(applicant.influencer_id, !next);
    };

    return (
        <Box
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER,
                borderRadius: 2.5,
                border: `1px solid ${selected ? `${BRAND_LUXE_PRIMARY}50` : 'rgba(255,255,255,0.05)'}`,
                px: 2,
                py: 1.5,
                transition: 'border-color 0.2s',
                '&:hover': { borderColor: selected ? `${BRAND_LUXE_PRIMARY}80` : 'rgba(255,201,101,0.15)' },
            }}>
            {/* ── Top row: checkbox + avatar + name + [desktop extras] + eng rate ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 } }}>
                {/* Checkbox */}
                {onToggleSelect && (
                    <Checkbox
                        checked={!!selected}
                        onChange={onToggleSelect}
                        size="small"
                        sx={{
                            flexShrink: 0,
                            p: 0.5,
                            color: 'rgba(255,255,255,0.3)',
                            '&.Mui-checked': { color: BRAND_LUXE_PRIMARY },
                            '& .MuiSvgIcon-root': { fontSize: 18 },
                        }}
                    />
                )}

                {/* Avatar */}
                <Avatar
                    src={applicant.avatar || undefined}
                    sx={{
                        width: { xs: 38, md: 44 },
                        height: { xs: 38, md: 44 },
                        flexShrink: 0,
                        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                        color: BRAND_LUXE_PRIMARY,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 14,
                        border: `2px solid ${BRAND_OUTLINE_VARIANT}30`,
                        ...influencerProfileAvatarSx,
                    }}>
                    {initials}
                </Avatar>

                {/* Name + handle */}
                <Box sx={{ flexShrink: 0, minWidth: 0, maxWidth: { xs: 120, md: 150 }, width: { md: 150 } }}>
                    <Typography
                        noWrap
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 13, md: 14 },
                            color: '#fff',
                            lineHeight: 1.2,
                        }}>
                        {applicant.name}
                    </Typography>
                    <Typography
                        noWrap
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 12,
                            color: 'rgba(255,255,255,0.4)',
                            mt: 0.25,
                        }}>
                        {applicant.handle}
                    </Typography>
                </Box>

                {/* Tags — always rendered as flex:1 spacer, chips conditional */}
                <Box sx={{ display: 'flex', gap: 0.5, flex: 1, minWidth: 0, flexWrap: 'wrap', alignContent: 'center' }}>
                    {safeTags.slice(0, 3).map((tag, i) => (
                        <Chip
                            key={i}
                            label={tagLabel(tag)}
                            size="small"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 10,
                                height: 22,
                                bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                color: 'rgba(255,255,255,0.6)',
                                border: 'none',
                            }}
                        />
                    ))}
                </Box>

                {/* Right group — always pinned to far right */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 }, flexShrink: 0 }}>
                    {/* Pitch — lg only */}
                    {applicant.pitch && (
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 12,
                                color: 'rgba(255,255,255,0.35)',
                                fontStyle: 'italic',
                                maxWidth: 220,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                display: { xs: 'none', lg: 'block' },
                            }}>
                            &ldquo;{applicant.pitch}&rdquo;
                        </Typography>
                    )}

                    {applicant.influencer_id && (
                        <IconButton
                            size="small"
                            aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
                            onClick={e => void handleFavorite(e)}
                            sx={{
                                color: favorited ? '#ff69b4' : 'rgba(255,255,255,0.45)',
                                '&:hover': { color: '#ff69b4', bgcolor: 'rgba(255,105,180,0.12)' },
                            }}>
                            {favorited ? (
                                <FavoriteIcon sx={{ fontSize: 18 }} />
                            ) : (
                                <FavoriteBorderIcon sx={{ fontSize: 18 }} />
                            )}
                        </IconButton>
                    )}

                    {/* Engagement rate */}
                    <Box sx={{ textAlign: 'right', minWidth: 56 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: 15, md: 16 },
                                color: BRAND_LUXE_SECONDARY,
                                lineHeight: 1,
                            }}>
                            {applicant.engagementRate ?? 0}%
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 9,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: 'rgba(255,255,255,0.3)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                            }}>
                            Eng. Rate
                        </Typography>
                    </Box>

                    {/* Preview video — md+ */}
                    {applicant.previewVideo && (
                        <Box
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                alignItems: 'center',
                                gap: 0.5,
                                bgcolor: 'rgba(255,255,255,0.06)',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 999,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                            }}>
                            <PlayCircleOutlineIcon sx={{ fontSize: 13, color: BRAND_LUXE_PRIMARY }} />
                            <Typography
                                sx={{ fontSize: 11, fontFamily: FONT_ENUM.MAIN_BOLD, color: BRAND_LUXE_PRIMARY }}>
                                Preview
                            </Typography>
                        </Box>
                    )}

                    {/* Actions — md+ */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={onReview}
                            size="small"
                            sx={{
                                borderRadius: 999,
                                textTransform: 'uppercase',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 10,
                                letterSpacing: '0.08em',
                                px: 2,
                                borderColor: BRAND_OUTLINE_VARIANT,
                                color: 'rgba(255,255,255,0.7)',
                                '&:hover': { borderColor: 'rgba(255,255,255,0.3)', bgcolor: 'rgba(255,255,255,0.04)' },
                            }}>
                            Review
                        </Button>
                        <Button
                            variant={isCancel ? 'outlined' : 'contained'}
                            onClick={onAccept}
                            disabled={isAccepting}
                            size="small"
                            sx={{
                                borderRadius: 999,
                                textTransform: 'uppercase',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 10,
                                letterSpacing: '0.08em',
                                px: 2,
                                ...(isCancel
                                    ? {
                                          borderColor: 'rgba(255,107,107,0.45)',
                                          color: '#ff8a80',
                                          '&:hover': {
                                              borderColor: 'rgba(255,107,107,0.7)',
                                              bgcolor: 'rgba(255,107,107,0.08)',
                                          },
                                      }
                                    : {
                                          bgcolor: BRAND_LUXE_PRIMARY,
                                          color: '#392700',
                                          boxShadow: 'none',
                                          '&:hover': {
                                              bgcolor: '#feb700',
                                              boxShadow: '0 4px 12px rgba(255,193,7,0.2)',
                                          },
                                          '&.Mui-disabled': {
                                              bgcolor: `${BRAND_LUXE_PRIMARY}50`,
                                              color: '#39270060',
                                          },
                                      }),
                            }}>
                            {isAccepting ? (
                                <CircularProgress
                                    size={12}
                                    thickness={5}
                                    sx={{ color: isCancel ? '#ff8a80' : '#392700' }}
                                />
                            ) : (
                                acceptLabel
                            )}
                        </Button>
                    </Box>
                    {/* end actions */}
                </Box>
                {/* end right group */}
            </Box>
            {/* end top row */}

            {/* ── Bottom row: actions — mobile only ── */}
            <Box
                sx={{
                    display: { xs: 'flex', md: 'none' },
                    justifyContent: 'flex-end',
                    gap: 1,
                    mt: 1.5,
                }}>
                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <Button
                        variant="outlined"
                        onClick={onReview}
                        size="small"
                        sx={{
                            borderRadius: 999,
                            textTransform: 'uppercase',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 10,
                            letterSpacing: '0.08em',
                            px: 1.5,
                            py: 0.4,
                            minWidth: 0,
                            borderColor: BRAND_OUTLINE_VARIANT,
                            color: 'rgba(255,255,255,0.7)',
                            '&:hover': { borderColor: 'rgba(255,255,255,0.3)', bgcolor: 'rgba(255,255,255,0.04)' },
                        }}>
                        Review
                    </Button>
                    <Button
                        variant={isCancel ? 'outlined' : 'contained'}
                        onClick={onAccept}
                        disabled={isAccepting}
                        size="small"
                        sx={{
                            borderRadius: 999,
                            textTransform: 'uppercase',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 10,
                            letterSpacing: '0.08em',
                            px: 1.5,
                            py: 0.4,
                            minWidth: 0,
                            ...(isCancel
                                ? {
                                      borderColor: 'rgba(255,107,107,0.45)',
                                      color: '#ff8a80',
                                      '&:hover': {
                                          borderColor: 'rgba(255,107,107,0.7)',
                                          bgcolor: 'rgba(255,107,107,0.08)',
                                      },
                                  }
                                : {
                                      bgcolor: BRAND_LUXE_PRIMARY,
                                      color: '#392700',
                                      boxShadow: 'none',
                                      '&:hover': { bgcolor: '#feb700' },
                                      '&.Mui-disabled': {
                                          bgcolor: `${BRAND_LUXE_PRIMARY}50`,
                                          color: '#39270060',
                                      },
                                  }),
                        }}>
                        {isAccepting ? (
                            <CircularProgress
                                size={12}
                                thickness={5}
                                sx={{ color: isCancel ? '#ff8a80' : '#392700' }}
                            />
                        ) : (
                            acceptLabel
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
